import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { taskOrgUnitId } from './epcScheduleDepartmentAssignment';

function numLevel(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function isSummary(t: EpcScheduleTaskRow | undefined): boolean {
  const s = t?.isSummary;
  return s === true || s === 'true' || s === 1 || s === '1';
}

/** Row indices of the subtree rooted at `i` (inclusive), by outline level. */
export function subtreeIndicesAt(tasks: EpcScheduleTaskRow[], i: number): number[] {
  const L = numLevel(tasks[i]?.outlineLevel);
  const out = [i];
  for (let j = i + 1; j < tasks.length && numLevel(tasks[j]?.outlineLevel) > L; j += 1) out.push(j);
  return out;
}

/** Toggle a row; summary rows toggle their whole subtree. Returns a new Set. */
export function toggleSelectionWithDescendants(
  tasks: EpcScheduleTaskRow[],
  selected: ReadonlySet<number>,
  i: number
): Set<number> {
  const next = new Set(selected);
  const block = isSummary(tasks[i]) ? subtreeIndicesAt(tasks, i) : [i];
  const turningOn = !selected.has(i);
  for (const j of block) {
    if (turningOn) next.add(j);
    else next.delete(j);
  }
  return next;
}

export type SelectionSummary = { lines: number; branches: number };

/** lines = all selected rows; branches = selected rows whose WBS parent is not itself selected. */
export function selectionSummary(
  tasks: EpcScheduleTaskRow[],
  selected: ReadonlySet<number>
): SelectionSummary {
  let branches = 0;
  for (const i of selected) {
    const L = numLevel(tasks[i]?.outlineLevel);
    let parent = -1;
    for (let j = i - 1; j >= 0; j -= 1) {
      if (numLevel(tasks[j]?.outlineLevel) < L) {
        parent = j;
        break;
      }
    }
    if (parent < 0 || !selected.has(parent)) branches += 1;
  }
  return { lines: selected.size, branches };
}

/** Selected rows that already carry a department different from `orgId` (drives the overwrite confirm). */
export function countOverwrites(
  tasks: EpcScheduleTaskRow[],
  selected: ReadonlySet<number>,
  orgId: string
): number {
  let n = 0;
  for (const i of selected) {
    const cur = taskOrgUnitId(tasks[i]!);
    if (cur && cur !== orgId) n += 1;
  }
  return n;
}
